import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { ArrowRight, AlertTriangle, Search, Shield, Users } from "lucide-react";

const commonRisks = [
  "Defective or fraudulent land titles",
  "Multiple sales of the same property",
  "Government acquisition of land",
  "Lack of proper documentation"
];

export default function DiasporaLegalGuidePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <main className="min-h-screen bg-[#FAFAF9]" data-testid="diaspora-legal-guide-page">
      <Navigation scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 bg-[#0A0A0B] relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
        <div className="absolute top-12 left-12 w-20 h-20 border-l border-t border-[#D4AF37]/15 hidden lg:block" />
        <div className="absolute bottom-12 right-12 w-20 h-20 border-r border-b border-[#D4AF37]/15 hidden lg:block" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <span className="text-[#D4AF37] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase font-semibold">
            Legal Insights
          </span>
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white mt-4 md:mt-6 leading-tight">
            Buying Property in Nigeria
            <span className="block text-[#D4AF37] italic mt-2">Legal Guide for Diaspora Investors</span>
          </h1>
          <div className="w-16 md:w-20 h-px bg-[#D4AF37] mx-auto mt-6 md:mt-8" />
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 md:py-24 bg-[#FAFAF9]">
        <div className="max-w-3xl mx-auto px-5 md:px-8 lg:px-12">
          <p className="text-[#0A0A0B]/70 text-base md:text-lg leading-relaxed mb-6">
            Buying property in Nigeria can be a highly rewarding investment, but it also carries 
            significant legal risks if proper due diligence is not conducted. Diaspora investors 
            often face challenges verifying property ownership, title authenticity, and compliance 
            with Nigerian land laws.
          </p>
          <p className="text-[#0A0A0B]/70 text-base md:text-lg leading-relaxed">
            At <span className="text-[#D4AF37] font-semibold">4ACES Attorneys</span>, we provide 
            strategic legal guidance designed to help investors acquire property securely and 
            avoid fraudulent transactions.
          </p>
        </div>
      </section>

      {/* Common Risks Section */}
      <section className="py-16 md:py-24 bg-[#0A0A0B]">
        <div className="max-w-4xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 border border-[#D4AF37]/30 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-white">
              Common Risks When Buying Property in Nigeria
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            {commonRisks.map((risk, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 p-5 md:p-6 border border-[#D4AF37]/20 bg-[#D4AF37]/5"
              >
                <div className="w-2 h-2 bg-[#D4AF37] rounded-full flex-shrink-0" />
                <span className="text-white/80 text-sm md:text-base">{risk}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Due Diligence Section */}
      <section className="py-16 md:py-24 bg-[#FAFAF9]">
        <div className="max-w-4xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 border border-[#D4AF37]/30 bg-[#0A0A0B] flex items-center justify-center">
              <Search className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-[#0A0A0B]">
              Legal Due Diligence Process
            </h2>
          </div>
          
          <div className="bg-white border border-[#0A0A0B]/10 p-6 md:p-8 lg:p-10">
            <p className="text-[#0A0A0B]/70 text-base md:text-lg leading-relaxed mb-6">
              Before any property acquisition, our firm conducts a comprehensive legal investigation 
              to ensure the transaction is secure and legally valid. This includes:
            </p>
            
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2.5 flex-shrink-0" />
                <span className="text-[#0A0A0B]/70">
                  <strong className="text-[#0A0A0B]">Independent Title Verification</strong> — 
                  Confirming the authenticity of land titles and ensuring the seller has the legal 
                  right to transfer ownership.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2.5 flex-shrink-0" />
                <span className="text-[#0A0A0B]/70">
                  <strong className="text-[#0A0A0B]">Land Registry Searches</strong> — 
                  Verifying records with the appropriate land registries to identify any encumbrances, 
                  government acquisitions, or competing ownership claims.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2.5 flex-shrink-0" />
                <span className="text-[#0A0A0B]/70">
                  <strong className="text-[#0A0A0B]">Ownership History Review</strong> — 
                  Tracing the chain of ownership to ensure the property has a clear and uncontested 
                  legal history.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Why Diaspora Need Legal Oversight */}
      <section className="py-16 md:py-24 bg-[#0A0A0B]">
        <div className="max-w-4xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 border border-[#D4AF37]/30 flex items-center justify-center">
              <Users className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-white">
              Why Diaspora Investors Need Legal Oversight
            </h2>
          </div>
          
          <div className="space-y-6">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              Purchasing property remotely presents unique challenges for diaspora investors. Without 
              being physically present in Nigeria, it is difficult to verify the condition of a property, 
              confirm the legitimacy of sellers, or navigate complex documentation requirements.
            </p>
            
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              Engaging a qualified property lawyer ensures that:
            </p>
            
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2.5 flex-shrink-0" />
                <span className="text-white/70">
                  All legal documentation is properly reviewed and verified before payments are made
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2.5 flex-shrink-0" />
                <span className="text-white/70">
                  The transaction is supervised from start to completion by a legal professional
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2.5 flex-shrink-0" />
                <span className="text-white/70">
                  Your interests are protected throughout the acquisition process
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2.5 flex-shrink-0" />
                <span className="text-white/70">
                  The property is properly registered in your name upon completion
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 md:py-24 lg:py-32 bg-[#FAFAF9] relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        
        <div className="max-w-3xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <div className="w-16 h-16 border border-[#D4AF37]/30 bg-[#0A0A0B] flex items-center justify-center mx-auto mb-8">
            <Shield className="w-8 h-8 text-[#D4AF37]" />
          </div>
          
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-[#0A0A0B] mb-6">
            Speak With a Property Lawyer
            <span className="block text-[#D4AF37] italic">Before You Invest</span>
          </h2>
          
          <p className="text-[#0A0A0B]/60 text-base md:text-lg mb-10 max-w-2xl mx-auto">
            Protect your investment with proper legal oversight. Schedule a confidential 
            consultation with our property law team.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/packages"
              className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#0A0A0B] px-10 py-5 text-sm tracking-[0.1em] font-bold hover:bg-[#C5A028] transition-all duration-300"
              data-testid="guide-cta-book"
            >
              Book a Private Consultation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
